import { Link, Head, usePage, Deferred } from '@inertiajs/react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Plus } from "lucide-react"
import { ColumnDef } from "@tanstack/react-table"
import { FlashMessage } from '@/components/flash-message'
import { SortableHeader } from '@/components/sortable-header'
import type { Supplier } from '@/types/serializers'
import { FilteredTable } from '@/components/ui/filtered-table'

interface Props {
  suppliers: Supplier[]
  [key: string]: any
}

const columns: ColumnDef<Supplier>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <SortableHeader column={column} title="Name" />,
    enableSorting: true,
    cell: ({ row }) => {
      const supplier = row.original
      return (
        <Link href={`/suppliers/${supplier.id}`} className="hover:underline">
          {supplier.name}
        </Link>
      )
    }
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => <SortableHeader column={column} title="Created" />,
    enableSorting: true,
    cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString()
  },
  {
    accessorKey: "updated_at",
    header: ({ column }) => <SortableHeader column={column} title="Updated" />,
    enableSorting: true,
    cell: ({ row }) => new Date(row.original.updated_at).toLocaleDateString()
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const supplier = row.original
      return (
        <div className="flex items-center gap-2">
          <Link href={`/suppliers/${supplier.id}`}>
            <Button variant="outline" size="sm">
              View
            </Button>
          </Link>
          <Link href={`/suppliers/${supplier.id}/edit`}>
            <Button variant="outline" size="sm">
              Edit
            </Button>
          </Link>
        </div>
      )
    },
  },
]

export default function Index() {
  const { suppliers } = usePage<Props>().props

  return (
    <>
      <Head title="Suppliers" />
      <div className="space-y-4 pt-4 px-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Suppliers</h1>
          <Link href="/suppliers/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Supplier
            </Button>
          </Link>
        </div>
      </div>

      <FlashMessage />

      <div className="p-6">
        <Deferred data="suppliers" fallback={<div>Loading...</div>}>
          <FilteredTable
            data={suppliers}
            columns={columns}
            searchPlaceholder="Search suppliers..."
          />
        </Deferred>
      </div>
    </>
  )
}
